"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveAwardTier = resolveAwardTier;
exports.shouldIssueAdvancedChoice = shouldIssueAdvancedChoice;
exports.resolveStrategyIds = resolveStrategyIds;
exports.resolveAdvancedChoiceStrategyId = resolveAdvancedChoiceStrategyId;
exports.resolveAwardedCardId = resolveAwardedCardId;
exports.resolveAwardedCardIds = resolveAwardedCardIds;
const campaign_config_1 = require("./campaign.config");
const types_1 = require("./types");
const ADVANCED_AWARDED_CARD = {
    nvidia: 'advanced-nvidia',
    btc: 'advanced-btc',
    hsbc: 'advanced-hsbc',
};
const FIXED_AWARDED_CARD = {
    15000000: '2000',
    40000000: '4000',
    100000000: '8000',
    500000000: '35000',
    1000000000: '65000',
    2000000000: '1000000',
};
function isChoiceTier(tier) {
    return tier.type === 'choice';
}
/** 入门 4 选 1。缺 group 时按 entry 兼容旧配置。 */
function findEntryChoiceTier(config) {
    return config.tiers.find((tier) => isChoiceTier(tier) && (tier.group ?? 'entry') === 'entry');
}
/** 8 万进阶 3 选 1。 */
function findAdvancedChoiceTier(config) {
    return config.tiers.find((tier) => isChoiceTier(tier) && tier.group === 'advanced');
}
function pickEnvText(item, isProdEnv) {
    if (!item) {
        return '';
    }
    return isProdEnv ? item.prod : item.test;
}
/** 取最高命中档。未达 1 万返回 null。 */
function resolveAwardTier(netHkdCents, config = campaign_config_1.nupCampaignConfig) {
    const hit = [...config.tiers]
        .filter((tier) => netHkdCents >= tier.minAmount)
        .sort((a, b) => b.minAmount - a.minAmount);
    return hit[0] ?? null;
}
/**
 * 是否发放 8 万进阶 3 选 1。
 * 最高命中档必须是 advanced choice：即 8 万 ≤ 净入金 < 15 万。
 * ≥15 万最高档变成现金券，进阶赏不再发。
 */
function shouldIssueAdvancedChoice(netHkdCents, config = campaign_config_1.nupCampaignConfig) {
    const top = resolveAwardTier(netHkdCents, config);
    return (!!top &&
        isChoiceTier(top) &&
        top.group === 'advanced' &&
        top.minAmount >= campaign_config_1.ADVANCED_CHOICE_MIN_AMOUNT_HKD_CENTS);
}
/**
 * 解析本轮要发的 benefit 策略。
 * ≥ 1 万必含入门 4 选 1；8 万到 15 万再追加进阶 3 选 1；
 * ≥ 15 万则追加最高现金券档，不再发 8 万三选一。
 */
function resolveStrategyIds(netHkdCents, choiceKey, config = campaign_config_1.nupCampaignConfig, isProdEnv = false, advancedChoiceKey = types_1.DEFAULT_ADVANCED_CHOICE_KEY) {
    if (netHkdCents < campaign_config_1.MIN_AWARD_AMOUNT_HKD_CENTS) {
        return [];
    }
    const ids = [];
    // 1. 入门 4 选 1：≥1 万必含
    const entryTier = findEntryChoiceTier(config);
    if (entryTier) {
        const choiceId = pickEnvText(entryTier.strategyIds[choiceKey], isProdEnv);
        if (choiceId) {
            ids.push(choiceId);
        }
    }
    // 2. 进阶 3 选 1：仅 8 万到 15 万；未选时调用方应先落到默认 nvidia
    if (shouldIssueAdvancedChoice(netHkdCents, config)) {
        const advancedId = resolveAdvancedChoiceStrategyId(advancedChoiceKey, config, isProdEnv);
        if (advancedId) {
            ids.push(advancedId);
        }
    }
    // 3. 现金券：只取最高固定档，与进阶 3 选 1 不会同时出现
    const top = resolveAwardTier(netHkdCents, config);
    if (top && top.type === 'fixed') {
        const fixedId = pickEnvText(top.strategyId, isProdEnv);
        if (fixedId) {
            ids.push(fixedId);
        }
    }
    return ids;
}
/** 解析 8 万档某个选项的策略 ID；未配置时返回空串。 */
function resolveAdvancedChoiceStrategyId(advancedChoiceKey, config = campaign_config_1.nupCampaignConfig, isProdEnv = false) {
    const advancedTier = findAdvancedChoiceTier(config);
    if (!advancedTier) {
        return '';
    }
    return pickEnvText(advancedTier.strategyIds[advancedChoiceKey], isProdEnv);
}
/** 按净入金档位解析获奖卡。未结算或未达门槛返回 null。 */
function resolveAwardedCardId(netHkdCents, choiceKey, config = campaign_config_1.nupCampaignConfig, advancedChoiceKey = types_1.DEFAULT_ADVANCED_CHOICE_KEY) {
    const ids = resolveAwardedCardIds(netHkdCents, choiceKey, config, advancedChoiceKey);
    return ids[ids.length - 1] ?? null;
}
/** 已获得卡列表：≥1 万含 4 选 1；8–15 万再叠加 3 选 1；≥15 万叠加最高现金券。 */
function resolveAwardedCardIds(netHkdCents, choiceKey, config = campaign_config_1.nupCampaignConfig, advancedChoiceKey = types_1.DEFAULT_ADVANCED_CHOICE_KEY) {
    if (netHkdCents == null || netHkdCents < campaign_config_1.MIN_AWARD_AMOUNT_HKD_CENTS) {
        return [];
    }
    const ids = [choiceKey];
    // 8–15 万叠一张进阶赏卡；≥15 万改叠现金券卡
    if (shouldIssueAdvancedChoice(netHkdCents, config)) {
        ids.push(ADVANCED_AWARDED_CARD[advancedChoiceKey]);
    }
    const top = resolveAwardTier(netHkdCents, config);
    if (top && top.type === 'fixed') {
        const voucher = FIXED_AWARDED_CARD[top.minAmount];
        if (voucher) {
            ids.push(voucher);
        }
    }
    return ids;
}
